import { Link, useLocation, useParams, useSearchParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";

const API_BASE_URL = "https://bluewaterdubai.websiteseoonline.com";
const BLOG_CATEGORY = "Latest News";
const FALLBACK_IMAGE = "/assets/img/blog/blog-s-1-1.jpg";

function buildApiUrl(pathname) {
  const baseUrl = (process.env.REACT_APP_API_BASE_URL || API_BASE_URL).replace(/\/$/, "");
  return baseUrl + pathname;
}

function imageWithVariant(image, variant, fallback = FALLBACK_IMAGE) {
  const value = String(image || "").trim();

  if (!value) {
    return fallback;
  }

  const normalized = value.startsWith("assets/") ? "/" + value : value;

  if (normalized.includes("imagedelivery.net") && !normalized.includes("/w=")) {
    return normalized.replace(/\/$/, "") + "/" + variant;
  }

  return normalized;
}

function escapeHtml(value) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function sanitizeBlogHtml(value) {
  const html = String(value || "").trim();

  if (!html) {
    return "";
  }

  const safeHtml = html
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "")
    .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, "")
    .replace(/\son\w+="[^"]*"/gi, "")
    .replace(/\son\w+='[^']*'/gi, "")
    .replace(/javascript:/gi, "");

  return /<[a-z][\s\S]*>/i.test(safeHtml) ? safeHtml : "<p>" + escapeHtml(safeHtml) + "</p>";
}

function blogDate(blog) {
  return String(blog?.date || "").trim();
}

function blogIdFromRoute(blogId, searchParams, location) {
  const queryId = searchParams.get("id") || searchParams.get("blogId") || searchParams.get("ref");

  if (queryId) {
    return decodeURIComponent(queryId).trim();
  }

  if (blogId) {
    return decodeURIComponent(blogId).trim();
  }

  const segments = location.pathname.split("/").filter(Boolean);
  const lastSegment = segments[segments.length - 1] || "";

  if (lastSegment && !lastSegment.toLowerCase().endsWith(".html") && lastSegment !== "blog") {
    return decodeURIComponent(lastSegment).trim();
  }

  return "";
}

async function fetchBlogs(signal) {
  const response = await fetch(buildApiUrl("/api/blogsapi.ashx"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      typ: BLOG_CATEGORY,
      id: "0",
    }),
    signal,
  });

  if (!response.ok) {
    throw new Error("Unable to load insights");
  }

  const payload = await response.json();
  return payload.result === 1 && Array.isArray(payload.data) ? payload.data : [];
}

async function fetchBlogDetails(id, signal) {
  const response = await fetch(buildApiUrl("/api/blogsapi.ashx"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ id }),
    signal,
  });

  if (!response.ok) {
    throw new Error("Unable to load insight");
  }

  const payload = await response.json();
  const data = payload.result === 1 && Array.isArray(payload.data) ? payload.data : [];
  return data[0] || null;
}

function BlogDetails() {
  const { blogId } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const resolvedBlogId = useMemo(() => blogIdFromRoute(blogId, searchParams, location), [blogId, searchParams, location]);
  const [blog, setBlog] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    const controller = new AbortController();

    async function loadBlog() {
      setStatus("loading");

      try {
        let id = resolvedBlogId;

        if (!id) {
          const blogs = await fetchBlogs(controller.signal);
          id = blogs[0]?.id || "";
        }

        if (!id) {
          setBlog(null);
          setStatus("ready");
          return;
        }

        const blogDetails = await fetchBlogDetails(id, controller.signal);
        setBlog(blogDetails);
        setStatus("ready");
      } catch (err) {
        if (err.name !== "AbortError") {
          setBlog(null);
          setStatus("error");
        }
      }
    }

    loadBlog();

    return () => controller.abort();
  }, [resolvedBlogId]);

  const description = sanitizeBlogHtml(blog?.Description || blog?.shortdescription || "");

  return (
    <>
      <div className="breadcumb-wrapper" style={{ backgroundImage: "url('/assets/img/bg/breadcrumb-bg.jpg')" }}>
        <div className="container">
          <div className="breadcumb-content">
            <h1 className="breadcumb-title">Blog</h1>
            <ul className="breadcumb-menu">
              <li>
                <Link to="/">Home</Link>
              </li>
              <li>
                <Link to="/blog">Blog</Link>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <section className="th-blog-wrapper blog-details space-top space-extra-bottom">
        <div className="container">
          {status === "loading" && <div className="text-center">Loading insight...</div>}
          {status === "error" && <div className="text-center">Unable to load insight.</div>}
          {status === "ready" && !blog && <div className="text-center">Insight not found.</div>}
          {status === "ready" && blog && (
            <div className="row justify-content-center">
              <div className="col-xxl-8 col-lg-8">
                <div className="th-blog blog-single">
                  <div className="blog-img">
                    <img src={imageWithVariant(blog.Image, "w=1200,sharpen=1")} alt={blog.title || "Insight"} />
                  </div>
                  <div className="blog-content">
                    <div className="blog-meta">
                      <Link to="/blog">
                        <i className="fa-light fa-user" />
                        Blue Water
                      </Link>
                      <Link to="/blog">
                        <i className="fa-regular fa-calendar" />
                        {blogDate(blog)}
                      </Link>
                      <Link to="/blog">
                        <i className="fa-regular fa-tag" />
                        Insights
                      </Link>
                    </div>
                    <h2 className="blog-title">{blog.title || "Latest Insight"}</h2>
                    {description ? (
                      <div className="blog-description" dangerouslySetInnerHTML={{ __html: description }} />
                    ) : (
                      <p>More details for this insight will be available soon.</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}

export default BlogDetails;
